import React from 'react';
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';

interface RunOverviewCardProps {
  run: {
    run_id: string;
    ticket_id: number;
    status: string;
    duration_ms: number | null;
    trigger: string;
    board_id: number | null;
    assignee_name?: string | null;
    priority_name?: string | null;
    type?: string | null;
    sub_type?: string | null;
    item?: string | null;
    dispatch_rule?: string | null;
    dispatch_result?: string | null;
    dispatch_error?: string | null;
  };
  boardName?: string;
}

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'info'> = {
  succeeded: 'success',
  success: 'success',
  failed: 'error',
  partial: 'warning',
  running: 'info',
};

const RunOverviewCard: React.FC<RunOverviewCardProps> = ({ run, boardName }) => {
  const statusColor = STATUS_COLORS[run.status] || 'default';

  return (
    <Card variant="outlined">
      <CardContent>
        {run.status === 'failed' && run.dispatch_error && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              bgcolor: 'error.light',
              color: 'error.contrastText',
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold">
              Run Failed
            </Typography>
            <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
              {run.dispatch_error}
            </Typography>
          </Box>
        )}

        <Typography variant="h6" gutterBottom>
          Run Overview
        </Typography>

        <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Run ID
            </Typography>
            <Typography variant="body2" fontFamily="monospace">
              {run.run_id.slice(0, 8)}...
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Ticket
            </Typography>
            <Typography variant="body2">#{run.ticket_id}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Box>
              <Chip label={run.status} color={statusColor} size="small" />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Duration
            </Typography>
            <Typography variant="body2">
              {run.duration_ms != null ? `${run.duration_ms}ms` : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Trigger
            </Typography>
            <Typography variant="body2">{run.trigger}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Board
            </Typography>
            <Typography variant="body2">{boardName || (run.board_id != null ? `#${run.board_id}` : '—')}</Typography>
          </Box>
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
          Outcome
        </Typography>
        <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(180px, 1fr))" gap={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Assigned to
            </Typography>
            <Typography variant="body2">{run.assignee_name || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Priority
            </Typography>
            <Typography variant="body2">{run.priority_name || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Type
            </Typography>
            <Typography variant="body2">{run.type || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              SubType
            </Typography>
            <Typography variant="body2">{run.sub_type || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Item
            </Typography>
            <Typography variant="body2">{run.item || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Rule
            </Typography>
            <Typography variant="body2">{run.dispatch_rule || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Dispatch result
            </Typography>
            <Typography variant="body2">{run.dispatch_result || '—'}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RunOverviewCard;
